<template>
  <AIBasePanel
    auto-scroll
    :title="getLocaleText('ai_boolean_creator_panel.toolbar_center_text')"
    @close="emit('close')"
    @home="emit('home')"
  >
    <AIAssistantBaseChat
      :messages="messages"
      @suggestion="handleSuggestion"
    >
      <template #modelControls="{ index, modelMessage }">
        <div v-if="modelMessage.stream.booleanQuery">
          <BooleanCreatorQueryResponse
            :disabled="index < messages.length - 1"
            :message="modelMessage"
            @refine="handleRefine($event, modelMessage)"
            @restart="handleRestart"
          />
        </div>
      </template>
    </AIAssistantBaseChat>

    <template #footer>
      <AIAssistantBaseChatInput
        ref="inputRef"
        v-model="input"
        data-testid="footer-container"
        :loading="loading"
        @submit="handleSubmit"
      >
        <template #after>
          <div class="tw:flex tw:items-center">
            <BaseButtonDropdown
              circle
              close-on-click
              :disabled="loading"
              icon="list"
              new-design
              new-icon
              :offset="[0, 10]"
              placement="top-end"
            >
              <BaseButtonList
                class="tw:py-1"
                :options="suggestOptions"
                @update:model-value="handleOption($event.label)"
              />
            </BaseButtonDropdown>
          </div>
        </template>
      </AIAssistantBaseChatInput>
    </template>
  </AIBasePanel>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import AIAssistantBaseChat from "shared/components/AI/AIAssistant/AIAssistantBaseChat.vue";
import AIAssistantBaseChatInput from "shared/components/AI/AIAssistant/AIAssistantBaseChatInput.vue";
import AIBasePanel from "shared/components/AI/AIBasePanel.vue";
import BooleanCreatorQueryResponse from "shared/components/AI/BooleanCreator/BooleanCreatorQueryResponse.vue";
import { BaseButtonDropdown, BaseButtonList } from "shared/components/base";
import {
  type AIAssistantModelMessage,
  type AIAssistantSuggestMessageOption,
  useAIAssistant,
} from "shared/composables/useAIAssistant";

const emit = defineEmits(["close", "home"]);

const { messages, loading, add, reset, submit } = useAIAssistant({
  json: true,
  source: "boolean creator",
  url: "ai/services/boolean_creator",
});

const input = ref<string>("");
const inputRef = useTemplateRef("inputRef");

const suggestOptions = computed<AIAssistantSuggestMessageOption[]>(() => [
  {
    icon: "list-edit",
    iconClass: "tw:text-dark-blue-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.industry"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.industry_tooltip"
    ),
    newIcon: true,
  },
  {
    icon: "list-edit",
    iconClass: "tw:text-dark-blue-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.brand_focus"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.brand_focus_tooltip"
    ),
    newIcon: true,
  },
  {
    icon: "list-edit",
    iconClass: "tw:text-dark-blue-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.media_outreach"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.media_outreach_tooltip"
    ),
    newIcon: true,
  },
  {
    icon: "list-edit",
    iconClass: "tw:text-dark-blue-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.ask_anything"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.ask_anything_tooltip"
    ),
    newIcon: true,
  },
  {
    icon: "list-edit",
    iconClass: "tw:text-dark-blue-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.teach_me"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.teach_me_tooltip"
    ),
    newIcon: true,
  },
  {
    icon: "wrench",
    iconClass: "tw:text-red-300",
    label: getLocaleText("ai_boolean_creator_panel.suggestions.fix"),
    tooltipText: getLocaleText(
      "ai_boolean_creator_panel.suggestions.fix_tooltip"
    ),
    newIcon: true,
  },
]);

function handleSuggestion(suggestion: string) {
  if (loading.value) return;
  submit(suggestion);
}

function addInitialMessages() {
  add([
    {
      role: "user",
      message: getLocaleText("ai_assistant_panel.options.create_boolean_query"),
    },
    {
      role: "suggest",
      message: getLocaleText("boolean_creator_chat.pre_suggestion_text"),
      options: suggestOptions.value,
    },
  ]);
}

function handleSubmit() {
  track("Send Message in Boolean Creator ", { prompt: input.value });
  submit(input.value);
  input.value = "";
}

function handleOption(prompt: string) {
  track("Select Template Button in Boolean Creator", { prompt });
  submit(prompt);
}

async function handleRefine(prompt: string, message: AIAssistantModelMessage) {
  track("Select Template Button in Boolean Creator", { prompt });

  if (message.stream.data.model_chat_history_id) {
    await updateRefinement(message.stream.data.model_chat_history_id, prompt);
  }

  submit(prompt);
}

async function updateRefinement(id: number, option: string) {
  try {
    await $streemApiV1.put(`ai/chat_histories/${id}`, {
      params: {
        refinements: [option],
      },
      transformKeys: true,
    });
  } catch (error) {
    ErrorDialog(
      getLocaleText("ai_boolean_creator_panel.error_setting_refinement"),
      error
    );
  }
}

function handleRestart() {
  reset();
  addInitialMessages();
}

watch(loading, async (value) => {
  if (value) return;
  await nextTick();
  inputRef.value?.focus();
});

onMounted(() => {
  addInitialMessages();
});
</script>
